import type { GridInitialState } from '@mui/x-data-grid-pro';

/**
 * Canonical column order for Issue DataGrid (matches columns.tsx).
 * Used to enforce consistent order across all users.
 */
const CANONICAL_ISSUE_COLUMN_ORDER: readonly string[] = [
  'orderNumber',
  'process',
  'issueNumber',
  'inspectionNumber',
  'title',
  'workflow',
  'stage',
  'sites',
  'levels',
  'targetCompletionDate',
  'createdAt',
  'modifiedAt',
  'createdBy',
  'modifiedBy',
  'finalCompletionDate',
  'detectionDate',
  'targetStartDate',
  'finalStartDate',
  'assignee',
  'executor',
  'subcontractors',
  'contractNumbers',
  'workTypes',
  'rootCauses',
  'hazardCategory',
  'executedByCompanies',
  'priority',
  'projectStage',
  'impact',
  'environmentalAspect',
  'effect',
  'decisionToImposeFine',
  'circumstances',
  'issueVerification',
  'delayReason',
  'rejectionReason',
  'proposedCorrectiveAction',
  'spilledSubstance',
  'estimatedCost',
  'finalCost',
  'expectedFine',
  'imposedFine',
  'targetAmount',
  'completedAmount',
  'amountPercentage',
  'numberOfEmployees',
  'daysOfInabilityToWork',
  'costCode',
  'personUnableToWork',
  'solutionProposal',
  'solutionMethod',
  'subcontractorRepresentative',
  'startDateDelay',
  'completionDateDelay',
  'responseToAuthorDelay',
  'targetResponseToAuthorDate',
  'finalResponseToAuthorDate',
  'sickLeaveStartDate',
  'sickLeaveEndDate',
  'supervisor',
  'victim',
  'victimJobTitle',
  'employeeNumber',
  'employmentType',
  'immediateMeasures',
  'victimActivity',
  'injuryTypes',
  'injuredBodyParts',
  'applicationNumber',
  'productType',
  'producerDistributor',
  'typeModel',
  'installationLocation',
  'requirements',
  'installationBasis',
  'constructionProductDocuments',
  'deviceDocuments',
  'additionalDocuments',
  'description',
  'state',
];

/**
 * Migration: Enforces canonical column order for Issue DataGrid.
 * Reorders user's columns to match the order defined in code.
 * Preserves all other column settings (dimensions, visibility, etc.).
 * Any columns not in the canonical list are appended at the end.
 */
export function enforceIssueColumnOrder(
  state: GridInitialState
): { next: GridInitialState; changed: boolean } {
  const original = state.columns?.orderedFields;

  if (!original?.length) {
    return { next: state, changed: false };
  }

  const canonicalSet = new Set(CANONICAL_ISSUE_COLUMN_ORDER);
  const originalSet = new Set(original);

  const ordered: string[] = [];

  for (const field of CANONICAL_ISSUE_COLUMN_ORDER) {
    if (originalSet.has(field)) {
      ordered.push(field);
    }
  }

  for (const field of original) {
    if (!canonicalSet.has(field)) {
      ordered.push(field);
    }
  }

  const isUnchanged = original.every((field, idx) => field === ordered[idx]);

  if (isUnchanged) {
    return { next: state, changed: false };
  }

  return {
    next: {
      ...state,
      columns: {
        ...state.columns,
        orderedFields: ordered,
      },
    },
    changed: true,
  };
}
